/**
 * Datadog Real User Monitoring (RUM) Configuration
 *
 * This module configures and initializes Datadog RUM for monitoring user interactions,
 * performance metrics, and application behavior in the Axle partner portal.
 *
 * Key Features:
 * - Automatic disabling in local development to prevent test data pollution
 * - Configurable override for local testing via environment variables
 * - Comprehensive tracking of user interactions, resources, and performance
 * - Session replay capabilities for debugging and user experience analysis
 *
 * Environment Variables Required:
 * - REACT_APP_DATADOG_RUM_APPLICATION_ID: Datadog application identifier
 * - REACT_APP_DATADOG_RUM_CLIENT_TOKEN: Client-side authentication token
 * - REACT_APP_DATADOG_IN_LOCAL: Optional override to enable in local development
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/
 */
import { PropagatorType, datadogRum } from '@datadog/browser-rum';

import { isLocal } from './utils/axleEnvironment';
import { graphQlPath } from './utils/axleUrls';
import { envVars } from './utils/envVars';

/**
 * Determines if Datadog monitoring should be enabled based on environment.
 *
 * Logic:
 * - Disabled by default in local development (isLocal = true)
 * - Can be overridden in local with REACT_APP_DATADOG_IN_LOCAL=true
 * - Always enabled in staging/production environments
 */
const datadogEnabled = !isLocal || envVars.datadogInLocal;

const propagatorTypes: PropagatorType[] = ['datadog', 'tracecontext'];
// NOTE: string `match` values are compared with `url.startsWith(match)` by the
// RUM SDK, so a bare domain like 'axlehealth.com' never matches a real request
// URL (which starts with 'https://api.axlehealth.com/...'). That silently
// dropped trace-header injection and broke RUM <-> APM trace linking. Use
// RegExp `match` (evaluated with .test() against the full URL) so any
// *.axlehealth(-dev|-staging).com subdomain — including the backend `api`
// subdomain — is treated as first-party.
const firstPartyHosts = [
    {
        match: /^https:\/\/([a-z0-9-]+\.)*axlehealth\.com/,
        propagatorTypes: propagatorTypes,
    },
    {
        match: /^https:\/\/([a-z0-9-]+\.)*axlehealth-dev\.com/,
        propagatorTypes: propagatorTypes,
    },
    {
        match: /^https:\/\/([a-z0-9-]+\.)*axlehealth-staging\.com/,
        propagatorTypes: propagatorTypes,
    },
];
if (isLocal) {
    firstPartyHosts.push({
        match: /^https?:\/\/localhost(:\d+)?/,
        propagatorTypes: propagatorTypes,
    });
}

/**
 * Detects the benign "ResizeObserver loop" browser warning.
 *
 * Browsers emit this through window.onerror when a ResizeObserver callback
 * changes the layout of an observed element within the same frame; delivery is
 * simply deferred to the next frame and nothing actually fails. It is fired by
 * our own observers as well as library internals (MUI, react-big-calendar), so
 * it is filtered centrally here rather than patched per-callsite.
 * Covers both the modern message and the legacy Chrome "loop limit exceeded"
 * variant.
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/
 */
const isBenignResizeObserverError = (message: string | undefined): boolean =>
    !!message &&
    /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(message);

/**
 * Matches credential-bearing query parameters wherever they appear in a string.
 * A failed request surfaces its raw URL — including tokens — not only in a
 * resource event's `url` field but also inside error `message` and `stack` text,
 * so scrubbing must operate on the string form rather than a parsed URL.
 *
 * The `_?` between compound words plus the `i` flag cover both snake_case and
 * camelCase spellings (`access_token`, `accessToken`). The value class stops at
 * `&`, whitespace, quotes, and common trailing punctuation so a token embedded
 * in prose or a `(file:line:col)` stack frame is masked without swallowing the
 * closing bracket or separator that follows it.
 */
const SENSITIVE_PARAM_PATTERN =
    /\b(access_?token|auth_?token|session_?token|refresh_?token|id_?token|api_?key|token|signature|secret|password|key|sig)=([^&\s"')\]};,]+)/gi;

/**
 * Masks the values of known-sensitive query parameters in any string. The
 * parameter name is kept so the request stays identifiable; only its value is
 * replaced.
 */
const redactSensitiveParams = (value: string): string =>
    value.replace(SENSITIVE_PARAM_PATTERN, (_match, name) => `${name}=REDACTED`);

/**
 * Initializes Datadog Real User Monitoring (RUM) with comprehensive tracking configuration.
 *
 * This function sets up client-side monitoring to collect:
 * - User interactions (clicks, page views, form submissions)
 * - Resource loading performance (API calls, assets, etc.)
 * - JavaScript errors and performance issues
 * - Session replays for debugging purposes
 *
 * The function automatically skips initialization in local development unless
 * explicitly overridden via environment variables to prevent test data from
 * polluting production monitoring dashboards.
 *
 * @returns {void} No return value - initializes global Datadog RUM instance
 *
 * @example
 * // Called once during application startup
 * initDatadog();
 *
 * @throws Will not throw errors - gracefully handles missing credentials
 */
export const initDatadog = () => {
    // Skip initialization in local development unless explicitly enabled
    if (!datadogEnabled) {
        console.info('[Datadog] Monitoring disabled for local development environment');
        return;
    }

    // Initialize Datadog RUM with comprehensive tracking configuration
    datadogRum.init({
        // Required: Application identification
        applicationId: envVars.datadogRumApplicationId,
        clientToken: envVars.datadogRumClientToken,

        // Datadog site configuration (US5 region)
        site: 'us5.datadoghq.com',

        // Service identification for filtering in Datadog dashboards
        service: 'dashboard',

        // Environment tag (development, staging, production)
        env: envVars.axleEnvironment,

        // Release version (short git SHA) for matching RUM error events to
        // source maps uploaded by @datadog/vite-plugin at build time.
        // Must match `errorTracking.sourcemaps.releaseVersion` in vite.config.ts.
        ...(envVars.releaseVersion ? { version: envVars.releaseVersion } : {}),

        // Sampling configuration
        sessionSampleRate: 100, // Collect data from 100% of sessions
        sessionReplaySampleRate: 100, // Record session replays for 100% of sessions
        traceSampleRate: 100, // Link 100% of frontend resources to backend traces (default is 20)
        traceContextInjection: 'all', // Inject trace headers on every matched request, not only sampled ones

        // Feature enablement flags
        trackUserInteractions: true, // Monitor clicks, form submissions, page navigation
        trackResources: true, // Monitor API calls, asset loading, network requests
        trackLongTasks: true, // Detect performance bottlenecks and blocking operations

        // Privacy configuration
        defaultPrivacyLevel: 'allow', // Allow collection of all data (can be 'mask' or 'mask-user-input')

        // GraphQL request tracking
        allowedGraphQlUrls: [
            {
                match: (url: string) => url.startsWith(graphQlPath),
                trackPayload: true, // Include GraphQL query (limited to 32 KB)
                trackResponseErrors: true, // Capture GraphQL errors from responses
            },
        ],
        allowedTracingUrls: firstPartyHosts,

        // Discard noise and scrub credentials before events reach Datadog
        beforeSend: (event) => {
            // The SDK stamps the current page URL onto every event type, so an
            // unauthenticated route that carries a secret in its query string
            // (e.g. /reset-password?token=…) would otherwise leak on all of them.
            if (event.view.url) {
                event.view.url = redactSensitiveParams(event.view.url);
            }
            if (event.view.referrer) {
                event.view.referrer = redactSensitiveParams(event.view.referrer);
            }
            if (event.type === 'error') {
                if (isBenignResizeObserverError(event.error.message)) {
                    return false;
                }
                if (event.error.message) {
                    event.error.message = redactSensitiveParams(event.error.message);
                }
                if (event.error.stack) {
                    event.error.stack = redactSensitiveParams(event.error.stack);
                }
                if (event.error.resource?.url) {
                    event.error.resource.url = redactSensitiveParams(event.error.resource.url);
                }
            }
            if (event.type === 'resource' && event.resource.url) {
                event.resource.url = redactSensitiveParams(event.resource.url);
            }
            return true;
        },
    });
};
